import { useEffect, useState } from 'react';
import { useAuth } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import { Modal } from '../../../../components/Modal';
import { PreviewGrid } from '../../../../components/PreviewGrid';
import { RichTextDocument } from '../../../../components/RichTextDocument';
import { Skeleton } from '../../../../components/Skeleton';
import { useToast } from '../../../../components/toastContext';
import { Textarea } from '../../../../components/inputs/Textarea';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { focusValidationError } from '../../../../lib/validationFocus';
import { COMPETENCY_LEVEL_LABELS } from '../../../human-resources/personnel/personnelTypes';
import { SURVEY_PLAN_STATUS, type SurveyDevelopmentNeed } from '../../../learning-development/survey/surveyTypes';
import { mySurveysApi } from '../mySurveysApi';
import type { MySurveyDevelopmentPlan } from '../mySurveyTypes';

const ID = 'my-survey-plan';

/** Reading the plan, or one of the two answers to it — ONE modal, never a dialog over a dialog. */
type Mode = 'view' | 'accept' | 'decline';

/**
 * The employee's APPROVED development plan — what the banner's "View development plan" opens —
 * with **Accept** and **Decline** (product call, 12 Oct 2026).
 *
 * **The answer is FINAL, once per approval.** Accept locks the plan for good; Decline needs a
 * REASON and reopens the plan for Learning & Development to revise and submit again. Once given,
 * the two buttons are gone and the plan states what was chosen and when.
 *
 * **Accept and Decline CHOOSE; Submit COMMITS.** The product's one word for a modal's write is
 * Submit, with the title naming the act — so choosing moves the SAME modal to that act, states
 * its consequence before the tick, and Submit sends it. Back returns to the plan without writing.
 *
 * NO Autogenerate: whether to accept one's own development plan, and why not, is the employee's
 * judgement — generating it would put words in their mouth.
 */
export function MySurveyDevelopmentPlanModal({
  surveyId,
  surveyActive,
  decidable = true,
  onClose,
  onDecided,
}: {
  surveyId: string;
  surveyActive: boolean;
  /**
   * False where the plan is only READ (product call, 13 Oct 2026): the employee's answer moved to
   * each training recommendation, so the page opens this without Accept or Decline. An answer
   * already given on the plan still shows.
   */
  decidable?: boolean;
  onClose: () => void;
  /** After an answer is saved — the page re-reads its stage. */
  onDecided: () => void;
}) {
  const { hasPermission } = useAuth();
  const toast = useToast();

  const [plan, setPlan] = useState<MySurveyDevelopmentPlan | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [mode, setMode] = useState<Mode>('view');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    mySurveysApi
      .developmentPlan(surveyId, controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setPlan(next);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;

        setLoadError(caught instanceof ApiError ? caught : new ApiError('Could not load your development plan.', 0));
      });

    return () => controller.abort();
  }, [surveyId]);

  // Answerable only while APPROVED and unanswered — the server refuses anything else anyway.
  const canDecide =
    decidable && plan !== null && plan.status === SURVEY_PLAN_STATUS.Approved && surveyActive && hasPermission('my-surveys.decide-plan');

  const choosing = mode !== 'view';
  const canSubmit = choosing && confirmed && !saving && (mode === 'accept' || reason.trim().length > 0);

  function choose(next: Mode) {
    setMode(next);
    setConfirmed(false);
    setError(null);
  }

  async function submit() {
    if (!canSubmit) return;

    setSaving(true);
    setError(null);

    try {
      if (mode === 'accept') {
        await mySurveysApi.acceptPlan(surveyId);
        toast.success('You accepted your development plan.');
      } else {
        await mySurveysApi.declinePlan(surveyId, reason.trim());
        toast.success('You declined your development plan. Learning & Development will see your reason.');
      }

      onDecided();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Your answer could not be saved.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: ID });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={mode === 'accept' ? 'Accept development plan' : mode === 'decline' ? 'Decline development plan' : 'Development plan'}
      size="xl"
      id={`${ID}-modal`}
      // The tick appears only once a decision is chosen — the strip appearing says nothing is left to fill.
      confirm={
        choosing ? { id: `${ID}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving } : undefined
      }
      footer={
        choosing ? (
          <>
            <Button id={`${ID}-back`} variant="secondary" onClick={() => choose('view')} disabled={saving}>
              Back
            </Button>
            <Button
              id={`${ID}-submit`}
              variant="primary"
              onClick={() => void submit()}
              disabled={!canSubmit}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </>
        ) : (
          <>
            <Button id={`${ID}-close`} variant="secondary" onClick={onClose}>
              Close
            </Button>
            {canDecide ? (
              <>
                <Button id={`${ID}-decline`} variant="secondary" onClick={() => choose('decline')}>
                  Decline
                </Button>
                <Button id={`${ID}-accept`} variant="primary" onClick={() => choose('accept')}>
                  Accept
                </Button>
              </>
            ) : null}
          </>
        )
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {loadError ? (
          <ErrorState message={loadError.message} correlationId={loadError.correlationId} />
        ) : !plan ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : mode === 'accept' ? (
          // The CONSEQUENCE, before the tick.
          <p className="text-sm text-slate-600">
            Accepting is final. Your development plan is settled as written, and it can no longer be changed or declined.
          </p>
        ) : mode === 'decline' ? (
          <>
            <p className="text-sm text-slate-600">
              Declining is final for this plan. It goes back to Learning &amp; Development with your reason, to be revised
              and approved again — you will be asked about the new plan then.
            </p>
            <Textarea
              id={`${ID}-reason`}
              label="Reason"
              required
              description="What should change in the plan. Learning & Development reads this when revising it."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              error={reason.trim() ? error?.fieldError('reason') : 'Say why you are declining.'}
              disabled={saving}
            />
          </>
        ) : (
          <>
            <DecisionNote plan={plan} />

            <div className="flex flex-col gap-2">
              <p className="text-sm text-slate-700">Identified development needs</p>
              <PreviewGrid<SurveyDevelopmentNeed>
                rows={plan.needs}
                rowKey={(n) => n.id}
                empty="No development needs were identified."
                columns={[
                  { header: 'Competency', width: 220, render: (n) => <TextCell value={n.competencyName} /> },
                  { header: 'Current level', width: 140, render: (n) => <TextCell value={COMPETENCY_LEVEL_LABELS[n.currentLevel]} /> },
                  { header: 'Target level', width: 140, render: (n) => <TextCell value={COMPETENCY_LEVEL_LABELS[n.targetLevel]} /> },
                  { header: 'Gap identified', width: 260, render: (n) => <TextCell value={n.gap ?? null} /> },
                ]}
              />
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-sm text-slate-700">Development plan</p>
              {/* The one renderer of stored markup — safe only because the server sanitised it on write. */}
              <RichTextDocument html={plan.plan ?? null} />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

/** What the employee already answered, if they have — the buttons are gone, so the plan says so. */
function DecisionNote({ plan }: { plan: MySurveyDevelopmentPlan }) {
  if (plan.status !== SURVEY_PLAN_STATUS.Accepted && plan.status !== SURVEY_PLAN_STATUS.Declined) return null;

  const accepted = plan.status === SURVEY_PLAN_STATUS.Accepted;

  return (
    <div role="status" className="flex flex-col gap-1 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <p className="text-xs text-slate-600">
        You {accepted ? 'accepted' : 'declined'} this development plan
        {plan.decidedAt != null ? (
          <>
            {' '}on{' '}
            <time dateTime={toIso(plan.decidedAt)} className="font-mono">
              {formatDateTime(plan.decidedAt)}
            </time>
          </>
        ) : null}
        .
      </p>
      {!accepted && plan.declineReason ? <p className="whitespace-pre-line text-sm text-slate-600">{plan.declineReason}</p> : null}
    </div>
  );
}
